import { Loader } from 'components/common/Loader';
import { useSetDropdownAndRole } from 'hooks/useSetDropdownAndRole';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  IIncidents,
  useGetIncidentsMutation,
  useGetCountsMutation,
  useGetLocationsMutation,
  useGetReportMutation,
  IIncidentReport,
} from 'redux/services/incident';
import { WorkFlowCard } from 'components/common/WorkFlowCard';
import { useTranslation } from 'react-i18next';
import { WorkFlowHeader } from 'components/common/WorkFlowHeader';
import { Pagination } from '@mui/material';
import { WORK_FLOW_OPTIONS } from 'pages/constants';
import {
  getAllSubTasks,
  getFilterValueByType,
  getSupportedFilterOptionForAPI,
} from 'pages/utils';
import { StartIncidentModal } from './StartIncidentModal';
import './Incident.scss';
import toast from 'react-hot-toast';
import { downloadIncidentReport } from 'pages/ReviewIncident/downloadIncidentReport';

const Incident = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [getIncidents, { data: response, isLoading: isIncidentsLoading }] =
    useGetIncidentsMutation();
  const [getCounts, { data: counts, isLoading: countsLoading }] =
    useGetCountsMutation();
  const [getLocations, { data: locations }] = useGetLocationsMutation();
  const { project, isLoading } = useSetDropdownAndRole();
  const [selectedIncident, setSelectedIncident] = useState<IIncidents | null>(
    null,
  );
  const [getReport] = useGetReportMutation();
  const [status, setStatus] = useState('');
  const [filterValue, setFilterValue] = useState<{
    id?: string;
    name: string;
  } | null>(null);
  const [filterByValue, setFilterByValue] = useState<string | null>(null);
  const [filterByValues, setFilterByValues] = useState<
    { id?: string; name: string }[]
  >([]);

  useEffect(() => {
    getIncidents({
      id: projectId,
      page: 1,
    });
  }, [getIncidents, projectId]);

  useEffect(() => {
    getCounts({ id: projectId });
    getLocations({ id: projectId });
  }, [getCounts, getLocations, projectId]);

  const getCardClickHandler = (incident: IIncidents) => () =>
    setSelectedIncident(incident);

  const getContinueDraftHandler = (item: IIncidents) => () => {
    navigate(`/project/${projectId}/record-incident/${item._id}/1`);
  };

  const resetState = () => {
    setFilterByValues([]);
    setFilterByValue(null);
    setFilterValue(null);
  };

  const handlePaginationChange = (
    _: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (response?.pagination?.page !== value) {
      getIncidents({
        id: projectId,
        page: value,
        status: status.toUpperCase(),
        filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
        filterValue: getFilterValueByType(filterValue, filterByValue),
      });
    }
  };

  const handleStatusChange = (changedStatus: string) => {
    setStatus(changedStatus.toUpperCase());
    getIncidents({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status: changedStatus.toUpperCase(),
      filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
      filterValue: getFilterValueByType(filterValue, filterByValue),
    });
  };
  const handleClearStatus = () => {
    setStatus('');
    getIncidents({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status: '',
      filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
      filterValue: getFilterValueByType(filterValue, filterByValue),
    });
  };
  const handleSelectOption = (option: string) => {
    setFilterByValue(option);
    if (filterValue) {
      setFilterValue(null);
      getIncidents({
        id: projectId,
        page: Number(response?.pagination?.page ?? 1),
      });
    }
    if (option === 'User') {
      setFilterByValues(project?.team ?? []);
    } else if (option === 'Tags') {
      setFilterByValues(locations?.map((name: string) => ({ name })) ?? []);
    } else if (option === 'Sub Project') {
      setFilterByValues(getAllSubTasks(project?.tasks ?? []));
    } else {
      resetState();
    }
  };

  const handleSearchSelect = (option: { id?: string; name: string } | null) => {
    const filterValue = getFilterValueByType(option, filterByValue);

    setFilterValue(option);
    getIncidents({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status,
      filterBy: filterValue
        ? getSupportedFilterOptionForAPI(filterByValue || '')
        : '',
      filterValue: filterValue || '',
    });
  };

  const handleClearSearchOptions = () => {
    resetState();
  };

  const handleCloseStartIncident = () => {
    setSelectedIncident(null);
  };

  const handleBack = () => navigate(`/project/${projectId}`);

  const getDownloadHandler = (incidentId: string) => async () => {
    toast.success('Report will be downloaded shortly');
    const res: any = await getReport({
      id: incidentId,
    });

    if (res.data as IIncidentReport) {
      downloadIncidentReport(res.data, project?.team ?? []);
    } else {
      toast.error('Report generation failed. Please try again later');
    }
  };

  return (
    <div className="incident">
      <h4 className="page-title">{t('header_incident')}</h4>
      {response?.data?.length || status || filterByValue ? (
        <WorkFlowHeader
          open={counts?.OPEN}
          closed={counts?.CLOSE}
          draft={counts?.DRAFT}
          options={WORK_FLOW_OPTIONS}
          selectedSearchOption={filterValue}
          selectedOption={filterByValue}
          searchOptions={filterByValues}
          status={status}
          isLoading={countsLoading}
          onStatusClick={handleStatusChange}
          onOptionClick={handleSelectOption}
          onSearchSelect={handleSearchSelect}
          onClearStatus={handleClearStatus}
          onClearSearchOptions={handleClearSearchOptions}
        />
      ) : null}

      <div className="setup-container gap-3">
        {(response?.data as IIncidents[])?.map((incident) => (
          <WorkFlowCard
            key={incident._id}
            title={incident.displayId ?? ''}
            role={project?.role ?? ''}
            date={moment(incident.updated_at || incident.created_at).format(
              'DD/MM/YYYY',
            )}
            status={incident.status}
            doer={
              project?.team?.find((teamObj) => teamObj.userId === incident.doer)
                ?.name ?? ''
            }
            inspector={
              project?.team?.find(
                (teamObj) => teamObj.userId === incident.inspector,
              )?.name ?? ''
            }
            projects={Object.values({
              subProject: incident.subProject?.name ?? '',
              subTask: incident.subTask?.name ?? '',
              endTask: incident.endTask?.name ?? '',
            }).filter((value) => value)}
            locations={incident.locations}
            onCardClick={getCardClickHandler(incident)}
            onContinueDraft={getContinueDraftHandler(incident)}
            onDownload={getDownloadHandler(incident._id ?? '')}
          />
        ))}
        {!isIncidentsLoading &&
        !response?.data?.length &&
        !status &&
        !filterByValue ? (
          <div className="d-flex justify-content-center w-100 flex-grow-1 align-items-center flex-column">
            <div className="no-incident-placeholder" />
            <span className="h6 text-center">{t('no_incident_text')}</span>
          </div>
        ) : (
          !isIncidentsLoading &&
          !response?.data?.length &&
          (status || filterByValue) && (
            <span className="d-flex justify-content-center align-items-center w-100 h5">
              {t('no_matching_results')}
            </span>
          )
        )}
      </div>
      <div className="d-flex justify-content-between my-1">
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
        >
          {t('btn_back')}
        </Button>
        {response?.pagination?.totalPage > 1 && (
          <Pagination
            className="align-self-end my-1"
            count={response?.pagination?.totalPage ?? 0}
            page={Number(response?.pagination?.page ?? 1)}
            color="primary"
            shape="rounded"
            onChange={handlePaginationChange}
          />
        )}
      </div>

      {(isLoading || isIncidentsLoading) && <Loader />}
      {selectedIncident && (
        <StartIncidentModal
          open
          incident={selectedIncident}
          onClose={handleCloseStartIncident}
        />
      )}
    </div>
  );
};

export default Incident;
